import CourseStatusLabel from "./CourseStatusLabel";
import EnterpriseOnlyCourseCallout from "../content/enterprise-only-course-callout.mdx";
import EnterprisePlanCallout from "../content/enterprise-plan-callout.mdx";
import LanguageSwitcher, {
  useLanguageSwitcher,
} from "./Lesson/LanguageSwitcher";
import React, {
  ComponentProps,
  ElementType,
  Fragment,
  PropsWithChildren,
  ReactElement,
  useEffect,
} from "react";
import pluralize from "pluralize";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FlexProps,
  Grid,
  HStack,
  Heading,
  HeadingProps,
  Img,
  LightMode,
  Link,
  List,
  ListIcon,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  CatalogButton,
  HOME_PAGE_CONTAINER_PROPS,
  HOME_PAGE_PX,
  HomePageHeader,
  HomePageTutorialGrid,
} from "./HomePage";
import { Changelog } from "./Changelog";
import { CourseButton } from "./CourseButton";
import { FiBookOpen } from "@react-icons/all-files/fi/FiBookOpen";
import { FiCalendar } from "@react-icons/all-files/fi/FiCalendar";
import { FiEdit3 } from "@react-icons/all-files/fi/FiEdit3";
import { FiFilm } from "@react-icons/all-files/fi/FiFilm";
import { FiHelpCircle } from "@react-icons/all-files/fi/FiHelpCircle";
import { FiZap } from "@react-icons/all-files/fi/FiZap";
import { Footer } from "./Footer";
import { Link as GatsbyLink } from "gatsby";
import { Helmet } from "react-helmet-async";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { PageSeo } from "./PageSeo";
import { RiDragDropLine } from "@react-icons/all-files/ri/RiDragDropLine";
import { TutorialImage } from "./Tutorials";
import { filterLessonsByLanguage, hasCourseAccess, useUser } from "@utils";
import { sortBy, uniq } from "lodash";
import { useLocalStorage } from "react-use";

const SPACING = "40px";

const formatList = (strings: string[]) => {
  try {
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat
    // hard coding locale as 'en' for now since we currently only support english
    const listFormatter = new Intl.ListFormat("en", {
      style: "long",
      type: "conjunction",
    });

    return listFormatter.format(strings);
  } catch {
    return strings.join(", ");
  }
};

type CourseDetailsWithIconProps = PropsWithChildren<{
  icon: ElementType;
}>;

export function CourseDetailsWithIcon({
  icon,
  children,
}: CourseDetailsWithIconProps) {
  return (
    <ListItem display="flex" alignItems="center">
      <ListIcon as={icon} />
      {children}
    </ListItem>
  );
}

function CourseHeading(props: HeadingProps) {
  return <Heading as="h3" fontSize="3xl" mb="3" {...props} />;
}

type CourseSectionProps = {
  heading: string;
} & FlexProps;

function CourseSection({ heading, children, ...props }: CourseSectionProps) {
  return (
    <Flex flexDirection="column" alignItems="flex-start" {...props}>
      <CourseHeading>{heading}</CourseHeading>
      {children}
    </Flex>
  );
}

const taskDetail: Record<string, ReactElement> = {
  Task: <CourseDetailsWithIcon icon={FiEdit3}>tasks</CourseDetailsWithIcon>,
  Editor: (
    <CourseDetailsWithIcon icon={FiZap}>code challenges</CourseDetailsWithIcon>
  ),
  Capsule: (
    <CourseDetailsWithIcon icon={FiZap}>code challenges</CourseDetailsWithIcon>
  ),
  MultipleChoice: (
    <CourseDetailsWithIcon icon={FiHelpCircle}>
      multiple choice questions
    </CourseDetailsWithIcon>
  ),
  FillInTheBlanks: (
    <CourseDetailsWithIcon icon={RiDragDropLine}>
      fill in the blank activities
    </CourseDetailsWithIcon>
  ),
  Video: <CourseDetailsWithIcon icon={FiFilm}>videos</CourseDetailsWithIcon>,
};

type RelatedCourses = ComponentProps<typeof HomePageTutorialGrid>["tutorials"];

type CoursePageProps = {
  course: {
    allVersions: ReadonlyArray<
      {
        lessons: ReadonlyArray<
          { tasks: ReadonlyArray<Pick<Queries.Task, "id">> } & Pick<
            Queries.Mdx,
            "slug"
          >
        >;
      } & Pick<
        Queries.Course,
        | "id"
        | "title"
        | "slug"
        | "unlisted"
        | "progressId"
        | "date"
        | "version"
        | "marketoProgramId"
        | "marketoLeadSourceDetail"
        | "memberships"
        | "tiers"
        | "showEnterprisePlanCallout"
        | "changelog"
        | "status"
        | "languages"
      >
    >;
    relatedCourses: RelatedCourses;
    certifications: ReadonlyArray<
      { courses: RelatedCourses } & Pick<
        Queries.Certification,
        "title" | "slug" | "simpleIcon"
      >
    >;
    lessons: ReadonlyArray<
      {
        frontmatter: Pick<
          Queries.MdxFrontmatter,
          "title" | "time" | "videoId" | "conditionalLanguage"
        > | null;
      } & Pick<Queries.Mdx, "id" | "tasks" | "slug">
    >;
  } & Pick<
    Queries.Course,
    | "id"
    | "title"
    | "date"
    | "description"
    | "unlisted"
    | "slug"
    | "progressId"
    | "seo"
    | "authors"
    | "thumb"
    | "prerequisites"
    | "learnings"
    | "type"
    | "memberships"
    | "tiers"
    | "showEnterprisePlanCallout"
    | "status"
    | "changelog"
    | "taskTypesComingSoon"
    | "marketoProgramId"
    | "marketoLeadSourceDetail"
    | "version"
    | "status"
    | "languages"
  >;
};

export default function CoursePage({ course }: CoursePageProps) {
  const { user } = useUser();
  const {
    title,
    seo,
    date,
    description,
    unlisted,
    authors,
    thumb,
    prerequisites,
    learnings,
    type,
    status,
    taskTypesComingSoon,
    lessons,
    certifications,
    relatedCourses,
    version,
    tiers,
    memberships,
    languages,
    showEnterprisePlanCallout,
  } = course;

  const badgeBgColor = useColorModeValue("blue.50", "blue.100");
  const bannerBgColor = useColorModeValue("blue.50", "black.200");

  useEffect(() => {
    if (window.location.hash) {
      const elem = document.querySelector(window.location.hash);
      if (elem) {
        elem.scrollIntoView();
      }
    }
  }, []);

  const [certification] = certifications;
  const userCertification =
    certification &&
    user?.certifications.find(
      (cert) => cert.certificationId === certification.slug
    );

  const hasVersions = course.allVersions.length > 1;

  const hasVideo = lessons.some((lesson) => lesson.frontmatter?.videoId);

  const courseTasks = uniq(
    lessons.flatMap((lesson) =>
      lesson.tasks.map((task) => {
        // NOTE: Both Editor and Capsule are code challenges.
        // We are slowly phasing out Editor components in favour of Capsule, but some courses are currently using both components, which results in a duplicate "code challenges" item.
        // The conditional check here prevents the duplicate item.
        if (task.type === "Editor") return "Capsule";
        return task.type;
      })
    )
  );

  const videoTime = lessons.reduce(
    (acc, lesson) => acc + (lesson.frontmatter?.time ?? 0),
    0
  );

  const certificationCourses = certifications.flatMap(
    (certification) => certification.courses
  );

  const allRelatedCourses = sortBy(
    uniq(relatedCourses.concat(certificationCourses)),
    "title"
  ).filter(({ id: courseId }) => courseId !== course.id);

  const showEnterpriseSignupCTA =
    course.tiers?.includes("ENTERPRISE") &&
    !hasCourseAccess({
      user,
      memberships,
      tiers,
    });

  const [language, setLanguage] = useLanguageSwitcher(
    course.slug,
    languages?.[0]
  );

  // filter lessons arr to remove lessons that aren't relavant to the selected language
  const filteredLessons = filterLessonsByLanguage(lessons, language);

  // build out Course JsonLd for the our structured data
  const itemListJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Course",
    name: title,
    description,
    provider: {
      "@type": "Organization",
      name: "Apollo GraphQL",
      sameAs: "https://apollographql.com",
    },
  });

  const noIndex = unlisted;

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{itemListJsonLd}</script>
      </Helmet>
      <PageSeo
        title={seo?.title ?? title}
        noIndex={!!noIndex}
        noFollow={!!noIndex}
        description={seo?.description ?? description ?? undefined}
        socialTitle={title}
        tagline={description ?? undefined}
        thumb={thumb}
      />
      <HomePageHeader bg={bannerBgColor} backdropFilter="blur(8px)" />
      <Box
        w="full"
        bgColor={bannerBgColor}
        borderBottomRadius={{ base: "48px", lg: "100px" }}
        py={{ md: "12", lg: "16" }}
        px={HOME_PAGE_PX}
      >
        <Center
          w="full"
          maxW="container.xl"
          mx="auto"
          flexDirection={{ base: "column", md: "row" }}
          flexFlow={{ base: "column-reverse", md: "row" }}
        >
          <Flex
            w={{ base: "full", md: "50%" }}
            mr={{ md: "56px" }}
            mt={{ base: "4", md: 0 }}
            mb={{ base: "6", md: 0 }}
            flexDir="column"
            alignItems={{ base: "center", md: "flex-start" }}
          >
            <Flex alignItems="center">
              <Text textStyle="smallCaps">{type}</Text>
              {status && <CourseStatusLabel status={status} />}
            </Flex>

            <Heading as="h2" mb="3" lineHeight={1} size="2xl">
              {title}
              {version && (
                <>
                  {" "}
                  <Badge variant="outline" lineHeight="shorter">
                    {version}
                  </Badge>
                </>
              )}
            </Heading>
            <Box fontSize="lg" textAlign={{ base: "center", md: "left" }}>
              <MarkdownRenderer>
                {seo?.description ?? description ?? ""}
              </MarkdownRenderer>
            </Box>
            {showEnterprisePlanCallout && (
              <Box mt={6}>
                <EnterprisePlanCallout />
              </Box>
            )}
            {showEnterpriseSignupCTA && (
              <Box mt={6}>
                <EnterpriseOnlyCourseCallout />
              </Box>
            )}
            {languages && languages.length > 1 && (
              <HStack mt={6}>
                <Text>Select a language:</Text>
                <LanguageSwitcher
                  activeLanguage={language}
                  languages={languages}
                  setLanguage={setLanguage}
                  eventSource="course_details"
                  courseId={course.progressId}
                />
              </HStack>
            )}
            <HStack spacing="4" mt={8}>
              {hasVersions && (
                <Button
                  as="a"
                  href="#changelog"
                  borderRadius={"full"}
                  variant="outline"
                >
                  View version history
                </Button>
              )}
              <CourseButton
                colorScheme="indigo"
                course={course}
                language={language}
              />
            </HStack>
          </Flex>
          <TutorialImage
            src={thumb?.src}
            alt={
              thumb
                ? thumb.alt
                : "Illustration of an astronaut holding the GraphQL logo in outerspace"
            }
            w={{ base: "full", md: "50%" }}
            rounded="3xl"
          />
        </Center>
      </Box>

      <Box sx={HOME_PAGE_CONTAINER_PROPS} mt={SPACING} mb="56px">
        {certification && (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            flexDir={{ base: "column", md: "row" }}
            w="full"
            borderWidth="1px"
            rounded="xl"
            p="6"
          >
            <Flex
              alignItems="center"
              flexDir={{ base: "column", md: "row" }}
              mb={{ base: "6", md: 0 }}
              mr={{ md: "6" }}
            >
              {certification.simpleIcon && (
                <Box boxSize="56px" mr={{ md: "4" }} mb={{ base: "4", md: 0 }}>
                  <Img
                    src={certification.simpleIcon}
                    alt={`${certification.title} badge`}
                    boxSize="full"
                  />
                </Box>
              )}
              <Box textAlign={{ base: "center", md: "left" }}>
                <Text
                  lineHeight="1.2"
                  fontSize="xl"
                  fontWeight="semibold"
                  mb="2"
                >
                  {title} is a certifying course
                </Text>
                <Text lineHeight="1.5">
                  {userCertification
                    ? `You have already completed this course and have received your Apollo ${certification.title} certification!`
                    : `By completing this course, you'll be on your way to
                  earning an Apollo ${certification.title} certification.`}
                </Text>
              </Box>
            </Flex>
            <Button
              as={GatsbyLink}
              to={`/certifications/${certification.slug}`}
              flexShrink={0}
              borderRadius={"full"}
            >
              View certification
            </Button>
          </Flex>
        )}

        <Grid
          w="full"
          alignItems="flex-start"
          justifyContent="space-between"
          gridTemplateColumns={{ base: "1fr", lg: "1fr 400px" }}
          gridColumnGap="120px"
          mt={SPACING}
        >
          <VStack spacing={SPACING} alignItems="flex-start">
            <CourseSection heading="Details">
              <List fontSize="lg" spacing="2">
                <CourseDetailsWithIcon icon={FiCalendar}>
                  Updated {date}
                </CourseDetailsWithIcon>
                <CourseDetailsWithIcon icon={FiBookOpen}>
                  {filteredLessons.length} lessons
                </CourseDetailsWithIcon>
              </List>
            </CourseSection>

            {learnings && (
              <CourseSection heading="What you'll learn">
                <UnorderedList fontSize="lg">
                  {learnings.map(
                    (learning, i) =>
                      learning && (
                        <ListItem key={i}>
                          <MarkdownRenderer>{learning}</MarkdownRenderer>
                        </ListItem>
                      )
                  )}
                </UnorderedList>
              </CourseSection>
            )}

            {prerequisites && (
              <CourseSection heading="Prerequisites">
                <UnorderedList fontSize="lg">
                  {languages && languages.length > 1 && (
                    <ListItem>
                      <Text>Available languages: {languages.join(", ")}</Text>
                    </ListItem>
                  )}
                  {prerequisites.map((prereq, i) => (
                    <ListItem key={i}>{prereq}</ListItem>
                  ))}
                </UnorderedList>
              </CourseSection>
            )}

            {authors && (
              <CourseSection heading={pluralize("Instructor", authors.length)}>
                <Flex
                  alignItems={{ base: "flex-start", md: "center" }}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <AvatarGroup size="md">
                    {authors.map(
                      (author) =>
                        author && (
                          <Avatar
                            key={author.name}
                            src={author.image || undefined}
                            name={author.name || undefined}
                          />
                        )
                    )}
                  </AvatarGroup>
                  <Text
                    ml={{ md: "4" }}
                    mt={{ base: "4", md: 0 }}
                    fontSize="lg"
                  >
                    {formatList(
                      authors
                        .map((author) => author?.name)
                        .filter(
                          (name): name is string => typeof name === "string"
                        )
                    )}
                  </Text>
                </Flex>
              </CourseSection>
            )}
          </VStack>

          <VStack alignItems="flex-start" spacing={SPACING} w="full">
            <CourseSection
              heading="Lesson List"
              w="full"
              mt={{ base: SPACING, lg: 0 }}
            >
              <List
                spacing="3"
                ml="0"
                borderWidth="1px"
                rounded="xl"
                p="6"
                w="full"
              >
                {filteredLessons.map((lesson) => {
                  return (
                    <ListItem key={lesson.slug}>
                      <Link
                        as={GatsbyLink}
                        to={`/${lesson.slug}`}
                        fontSize="lg"
                      >
                        {lesson.frontmatter?.title}
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </CourseSection>

            <CourseSection heading="This tutorial includes" w="full">
              <List
                spacing="4"
                ml="0"
                borderWidth="1px"
                rounded="xl"
                p="6"
                w="full"
                fontSize="lg"
              >
                {status === "beta" && (
                  <Heading size="md">
                    Currently in
                    <CourseStatusLabel status={status} />
                  </Heading>
                )}
                {hasVideo && (
                  <CourseDetailsWithIcon icon={FiFilm}>
                    videos ({videoTime} min total)
                  </CourseDetailsWithIcon>
                )}
                {courseTasks.map((task) => (
                  <Fragment key={task}>{taskDetail[task]}</Fragment>
                ))}
                {taskTypesComingSoon && taskTypesComingSoon.length > 0 && (
                  <>
                    <Divider />
                    <Heading size="md">Coming soon</Heading>
                    <List spacing="2" color="inactive">
                      {taskTypesComingSoon.map((task) => (
                        <ListItem key={task}>{taskDetail[task]}</ListItem>
                      ))}
                    </List>
                  </>
                )}
              </List>
            </CourseSection>
          </VStack>
        </Grid>
        {hasVersions && (
          <Box mt={SPACING}>
            <CourseHeading mb="1" scrollMarginTop="16" id="changelog">
              Version history
            </CourseHeading>
            <Text fontSize="lg" mb="4">
              If you take multiple versions of the same course, your progress
              for each version is saved separately.
            </Text>
            <Box borderWidth={1} rounded="lg">
              <Table>
                <Thead
                  sx={{
                    th: {
                      fontFamily: "mono",
                      fontWeight: "semibold",
                    },
                  }}
                >
                  <Tr>
                    <Th>Version</Th>
                    <Th>Last updated</Th>
                    <Th>Changes</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody fontSize="lg">
                  {course.allVersions.map((version, index, array) => (
                    <Tr
                      key={version.id}
                      sx={{
                        td: {
                          borderBottomWidth: index < array.length - 1 ? 1 : 0,
                        },
                      }}
                    >
                      <Td>
                        <Link as={GatsbyLink} to={`/${version.slug}`}>
                          {version.slug.includes("/") ? (
                            version.version
                          ) : (
                            <>
                              Current version
                              <Badge
                                ml="3"
                                rounded="md"
                                fontWeight="semibold"
                                color="gray.900"
                                bg={badgeBgColor}
                                px="2"
                              >
                                Recommended
                              </Badge>
                            </>
                          )}
                        </Link>
                      </Td>
                      <Td>{version.date}</Td>
                      <Td>
                        {version.changelog && (
                          <Changelog changes={version.changelog} />
                        )}
                      </Td>
                      <Td textAlign="right">
                        <CourseButton
                          colorScheme="indigo"
                          course={version}
                          subject="version"
                          language={language}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
        {allRelatedCourses.length > 0 && (
          <Box mt={SPACING}>
            <CourseHeading>Related tutorials</CourseHeading>
            <HomePageTutorialGrid
              tutorials={allRelatedCourses}
              spacingX="4"
              spacingY="6"
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
            />
            <CatalogButton mt="12" />
          </Box>
        )}
      </Box>
      <Footer />
    </>
  );
}
